import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Skeleton,
} from "@mui/material";
import {
  LocationOn as LocationOnIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { BACKGROUND_COLORS, ESTADO_ORO_COLORS } from "../../style/colors.js";
import { getMetalsData, getMetalsNeedsData, getCordobaStockData, getExcelDetailsData } from "../../services/ConnectDataBase.js";
import { SCREEN_IDS, typeConnect } from "../../constants/Constants.js";
import { calculateFineMetals } from "../../constants/MetalPurityFactors.js";
import ComponentExcelFilters from "../Metales/ComponentExcelFilters.js";
import ComponentMetalsCalendar from "../Metales/ComponentMetalsCalendar.js";

// Configuración de skeletons de carga
const SKELETON_CONFIG = {
  summaryRows: 3,        // Número de filas en la tabla de resumen (2 datos + 1 total)
  tableRows: 5,          // Número de filas en las tablas de ubicación
  height: 20,            // Altura de los skeletons en px
  widthLabel: "100%",    // Ancho del skeleton de etiqueta
  widthValue: "100%",    // Ancho del skeleton de valor
};

export default function ComponentEstadoOro() {
  const [cordobaData, setCordobaData] = useState({
    goldStock: [],
    findingsStock: [],
    workInProgress: [],
  });

  const [indiaData, setIndiaData] = useState({
    goldStock: [],
    findingsStock: [],
    platinumStock: [],
  });

  const [excelDetailsData, setExcelDetailsData] = useState([]);
  const [dynamicRequiredMetals, setDynamicRequiredMetals] = useState({
    cordoba: { gold: 0, platinum: 0 },
    euroshine: { gold: 0, platinum: 0 }
  });
  const [lastUpdate, setLastUpdate] = useState(new Date().toLocaleString());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTables, setExpandedTables] = useState({
    "Gold Stock": false,
    "Platinum Stock": false,
    "Findings Stock": false,
    "Stock de Oro": false,
    "Stock de Fornituras": false,
    "Trabajo en Proceso": false,
  });
  const [isIndiaExpanded, setIsIndiaExpanded] = useState(false);

  useEffect(() => {
    const loadMetalsData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const metalsData = await getMetalsData(
          SCREEN_IDS.ESTADO_METALES,
          typeConnect[0]
        );

        const needsData = await getMetalsNeedsData(
          SCREEN_IDS.ESTADO_METALES,
          typeConnect[0]
        );
        const cordobaStockData = await getCordobaStockData(
          SCREEN_IDS.ESTADO_METALES,
          typeConnect[0]
        );

        const excelDetails = await getExcelDetailsData(
          SCREEN_IDS.ESTADO_METALES,
          typeConnect[0]
        );


        if (metalsData) {
          setCordobaData({
            goldStock: metalsData.cordoba?.goldStock || [],
            findingsStock: metalsData.cordoba?.findingsStock || [],
            workInProgress: metalsData.cordoba?.workInProgress || [],
            needs: needsData?.Cordoba || {},
            stock: cordobaStockData || []
          });
          if (
            metalsData["Gold Stock"] ||
            metalsData["Findings Stock"] ||
            metalsData["Platinum Stock"]
          ) {
            setIndiaData({
              goldStock: metalsData["Gold Stock"] || [],
              findingsStock: metalsData["Findings Stock"] || [],
              platinumStock: metalsData["Platinum Stock"] || [],
              needs: needsData?.Euroshine || {}
            });
          }
          
          if (excelDetails && Array.isArray(excelDetails)) {
            setExcelDetailsData(excelDetails);
          }
          
          setLastUpdate(new Date().toLocaleString());
        } else {
          setError("No se pudieron cargar los datos de metales");
        }
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError("Error al conectar con el servidor");
      } finally {
        setIsLoading(false);
      }
    };
    loadMetalsData();
  }, []);

  const calculateTotals = (data, tableType = "standard") => {
    if (!data || !Array.isArray(data)) {
      return tableType === "findings"
        ? { clsQty: 0, clsWt: 0, pureWt: 0, clsValue: 0 }
        : { clsWt: 0, pureWt: 0 };
    }

    return data.reduce(
      (totals, item) => {
        let clsWt = 0;
        if (!isNaN(item.Cls_Wt)) {
          clsWt = parseFloat(item.Cls_Wt);
        }

        let pureWt = 0;
        if (tableType === "findings") {
          pureWt = clsWt;

          // Calcular cantidad para findings
          let clsQty = 0;
          if (!isNaN(item.Cls_Qty)) {
            clsQty = parseFloat(item.Cls_Qty);
          }
          totals.clsQty += clsQty;

          // Calcular valor para findings
          let clsValue = 0;
          if (!isNaN(item.Cls_Value)) {
            clsValue = parseFloat(item.Cls_Value);
          }
          totals.clsValue += clsValue;
        } else {
          if (!isNaN(item.Pure_Wt)) {
            pureWt = parseFloat(item.Pure_Wt);
          }
        }

        totals.clsWt += clsWt;
        totals.pureWt += pureWt;
        return totals;
      },
      tableType === "findings"
        ? { clsQty: 0, clsWt: 0, pureWt: 0, clsValue: 0 }
        : { clsWt: 0, pureWt: 0 }
    );
  };

  const renderTable = (
    title,
    data,
    color = "primary",
    tableType = "standard"
  ) => {
    // Calcular totales por tipo de findings si es una tabla de findings
    const getFindingsByType = (findingsData) => {
      const goldFindings = findingsData.filter(item => item.Fnd_Type === "Gold Findings");
      const silverFindings = findingsData.filter(item => item.Fnd_Type === "Silver Findings");
      const platinumFindings = findingsData.filter(item => item.Fnd_Type === "Platinum Findings");
      const otherFindings = findingsData.filter(item => item.Fnd_Type === "Other Findings");
      
      return {
        gold: calculateTotals(goldFindings, "findings"),
        silver: calculateTotals(silverFindings, "findings"),
        platinum: calculateTotals(platinumFindings, "findings"),
        other: calculateTotals(otherFindings, "findings")
      };
    };

    const findingsByType = tableType === "findings" ? getFindingsByType(data) : null;
    
    // Guardar el total original antes del filtrado para la lógica de expansión
    const originalDataLength = data.length;
    
    // Filtrar filas que contengan "Total" en Loc_Desc (vienen del servidor con NaN)
    let filteredData = data.filter(
      (item) =>
        !item.Loc_Desc ||
        (typeof item.Loc_Desc === "string" &&
          !item.Loc_Desc.toLowerCase().includes("total"))
    );

    // Filtrar filas con cantidad cero para tablas de findings
    if (tableType === "findings") {
      filteredData = filteredData.filter(
        (item) => {
          const cantidad = parseFloat(item.Cls_Qty || 0);
          return cantidad > 0;
        }
      );
    }

    // Ordenar por tipo de findings si es una tabla de findings
    if (tableType === "findings") {
      filteredData = filteredData.sort((a, b) => {
        const typeA = a.Fnd_Type || "";
        const typeB = b.Fnd_Type || "";
        return typeA.localeCompare(typeB);
      });
    }

    const displayData =
      !expandedTables[title] && originalDataLength > 10
        ? filteredData.slice(0, 10)
        : filteredData;

    const totals = calculateTotals(filteredData, tableType);
    const showTotals = expandedTables[title] || originalDataLength <= 10;

    const getHeaders = () => {
      switch (tableType) {
        case "findings":
          return [
            { key: "Fnd_Type", label: "Tipo", width: 100, align: "left" },
            { key: "RM_Code", label: "Código", width: 80, align: "left" },
            { key: "Cls_Qty", label: "Cant.", width: 70, align: "right" },
            { key: "Cls_Wt", label: "Peso (g)", width: 80, align: "right" },
            { key: "Cls_Value", label: "Valor", width: 80, align: "right" },
          ];
        case "gold":
        case "platinum":
        default:
          return [
            { key: "Loc_Desc", label: "Ubicación", width: 150, align: "left" },
            {
              key: "Cls_Wt",
              label: "Total (g)",
              width: 80,
              align: "right",
            },
            {
              key: "Pure_Wt",
              label: "Puro (g)",
              width: 80,
              align: "right",
            },
          ];
      }
    };

    const headers = getHeaders();

    return (
      <Card sx={{ mb: 0.5, boxShadow: 3, borderRadius: 2, minHeight: 400 }}>
        <CardContent sx={{ p: 1.5, height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Sección superior con altura fija */}
          <Box sx={{ minHeight: 40, mb: 0.2 }}>
          {/* Primera línea: Título y controles */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <MaterialCommunityIcons 
              name="gold" 
                size={14} 
              style={{ 
                  marginRight: 4,
                color: color === 'primary' ? ESTADO_ORO_COLORS.ICON_PRIMARY : ESTADO_ORO_COLORS.ICON_SECONDARY
              }}
            />
            <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", color: `${color}.main`, fontSize: "0.75rem" }}
            >
              {title}
            </Typography>
              {originalDataLength > 10 ? (
              <IconButton
                onClick={() =>
                  setExpandedTables((prev) => ({
                    ...prev,
                    [title]: !prev[title],
                  }))
                }
                sx={{ ml: 1, color: `${color}.main` }}
                size="small"
              >
                {expandedTables[title] ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
              ) : (
                <Box sx={{ ml: 1, width: 24, height: 24 }} />
            )}
              {originalDataLength > 10 ? (
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                {expandedTables[title]
                  ? `Mostrando todos (${originalDataLength})`
                  : `Mostrando 10 de ${originalDataLength}`}
              </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{ ml: 1, color: "transparent" }}
                >
                  Mostrando 10 de 10
              </Typography>
            )}
          </Box>
          
            {/* Segunda línea: Chips por tipo de tabla */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.1, flexWrap: "wrap", gap: 0.2 }}>
              {tableType === "findings" && findingsByType ? (
                <>
              <Chip
                label={`Gold: ${findingsByType.gold.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
              />
              <Chip
                label={`Silver: ${findingsByType.silver.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
              />
              <Chip
                label={`Platinum: ${findingsByType.platinum.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
              />
              <Chip
                label={`Other: ${findingsByType.other.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                </>
              ) : tableType === "gold" ? (
                <>
                  <Chip
                    label={`Total: ${totals.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                  <Chip
                    label={`Puro: ${totals.pureWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                </>
              ) : tableType === "platinum" ? (
                <>
                  <Chip
                    label={`Total: ${totals.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                  <Chip
                    label={`Puro: ${totals.pureWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                </>
              ) : (
                // Para tablas estándar (como work in progress)
                <>
                  <Chip
                    label={`Total: ${totals.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                  <Chip
                    label={`Puro: ${totals.pureWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                  <Chip
                    label=""
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px", visibility: "hidden" }}
                  />
                  <Chip
                    label=""
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px", visibility: "hidden" }}
                  />
                </>
              )}
            </Box>
          </Box>

          <TableContainer 
            component={Paper} 
            sx={{ 
              flexGrow: 1,
              borderRadius: 1,
              border: `1px solid ${ESTADO_ORO_COLORS.TABLE_BORDER}`,
              overflow: "hidden"
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 400 }}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell
                      key={header.key}
                      sx={{
                        backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY,
                        fontWeight: "bold !important",
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif !important',
                        minWidth: header.width,
                        textAlign: header.align || "left",
                        fontSize: "0.8rem",
                        padding: "4px 8px",
                        color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT
                      }}
                    >
                      {header.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.map((row, index) => (
                  <TableRow key={index} hover>
                    {headers.map((header) => (
                      <TableCell
                        key={header.key}
                        align={header.align || "left"}
                        sx={{
                          fontFamily:
                            header.key.includes("Wt") ||
                            header.key.includes("Value") ||
                            header.key.includes("Qty")
                              ? "monospace !important"
                              : '"Roboto", "Helvetica", "Arial", sans-serif !important',
                          fontWeight:
                            header.key === "Pure_Wt"
                              ? "bold !important"
                              : "medium !important",
                          color:
                            header.key === "Pure_Wt"
                              ? "success.main !important"
                              : "inherit !important",
                          fontSize: "0.8rem",
                          padding: "4px 8px"
                        }}
                      >
                        {header.key.includes("Wt") ||
                        header.key.includes("Value") ||
                        header.key.includes("Qty")
                          ? parseFloat(row[header.key] || 0).toLocaleString(
                              "es-ES",
                              {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                              }
                            )
                          : row[header.key] || ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {showTotals && (
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    {tableType === "findings" ? (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily:
                              '"Roboto", "Helvetica", "Arial", sans-serif !important',
                            fontSize: "0.8rem",
                            padding: "4px 8px"
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.clsQty.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.clsWt.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            color: "success.main !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.clsValue.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily:
                              '"Roboto", "Helvetica", "Arial", sans-serif !important',
                            fontSize: "0.8rem",
                            padding: "4px 8px"
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.clsWt.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            color: "success.main !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.pureWt.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderLocationSection = (locationName, data, color) => {
    // Calcular totales por tipo de metal
    const goldTotal = calculateTotals(data.goldStock || [], "gold");
    const platinumTotal = calculateTotals(data.platinumStock || [], "platinum");
    const findingsTotal = calculateTotals(data.findingsStock || [], "findings");
    const workInProgressTotal = calculateTotals(data.workInProgress || [], "standard");

    // Calcular totales por tipo de findings
    const getFindingsByType = (findingsData) => {
      const goldFindings = findingsData.filter(item => item.Fnd_Type === "Gold Findings");
      const silverFindings = findingsData.filter(item => item.Fnd_Type === "Silver Findings");
      const platinumFindings = findingsData.filter(item => item.Fnd_Type === "Platinum Findings");
      const otherFindings = findingsData.filter(item => item.Fnd_Type === "Other Findings");
      
      return {
        gold: calculateTotals(goldFindings, "findings"),
        silver: calculateTotals(silverFindings, "findings"),
        platinum: calculateTotals(platinumFindings, "findings"),
        other: calculateTotals(otherFindings, "findings")
      };
    };

    const findingsByType = getFindingsByType(data.findingsStock || []);

    return (
      <Box sx={{ flex: 1, p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <LocationOnIcon sx={{ mr: 1, color: ESTADO_ORO_COLORS.LOCATION_ICON }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: ESTADO_ORO_COLORS.LOCATION_TEXT,
              textTransform: "uppercase",
              fontSize: "1.1rem"
            }}
          >
            {locationName}
          </Typography>
          
          {/* Flecha de expandir/colapsar solo para India */}
          {locationName === "India" && (
            <IconButton
              onClick={() => setIsIndiaExpanded(!isIndiaExpanded)}
              size="small"
              sx={{ 
                ml: 1,
                p: 0.5,
                color: ESTADO_ORO_COLORS.LOCATION_ICON,
                "&:hover": {
                  backgroundColor: "rgba(44, 62, 80, 0.1)"
                }
              }}
            >
              {isIndiaExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          
          {/* Mostrar totales separados por tipo de metal */}
          {locationName === "India" ? (
            <>
              <Chip
                label={`Oro: ${goldTotal.pureWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                variant="filled"
                size="small"
                sx={{ 
                  fontWeight: "bold", 
                  fontSize: "0.6rem", 
                  height: "18px",
                  backgroundColor: ESTADO_ORO_COLORS.CHIP_GOLD_BG,
                  color: ESTADO_ORO_COLORS.CHIP_GOLD_TEXT,
                  border: `1px solid ${ESTADO_ORO_COLORS.CHIP_GOLD_BORDER}`
                }}
              />
              <Chip
                label={`Platino: ${platinumTotal.pureWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                variant="filled"
                size="small"
                sx={{ 
                  fontWeight: "bold", 
                  fontSize: "0.6rem", 
                  height: "18px",
                  backgroundColor: ESTADO_ORO_COLORS.CHIP_PLATINUM_BG,
                  color: ESTADO_ORO_COLORS.CHIP_PLATINUM_TEXT,
                  border: `1px solid ${ESTADO_ORO_COLORS.CHIP_PLATINUM_BORDER}`
                }}
              />
            </>
          ) : (
            <>
              <Chip
                label={`Oro: ${goldTotal.pureWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px" }}
              />
              <Chip
                label={`Fornituras: ${findingsTotal.clsWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px" }}
              />
              <Chip
                label={`WIP: ${workInProgressTotal.pureWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px" }}
              />
            </>
          )}
        </Box>

        {locationName === "India" ? (
          isIndiaExpanded && (
            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", lg: "row" },
              gap: 1,
              flexWrap: "wrap"
            }}>
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 33%" } }}>
              {renderTable("Gold Stock", data.goldStock || [], color, "gold")}
              </Box>
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 33%" } }}>
              {renderTable(
                "Platinum Stock",
                data.platinumStock || [],
                color,
                "platinum"
              )}
              </Box>
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 33%" } }}>
              {renderTable(
                "Findings Stock",
                data.findingsStock || [],
                color,
                "findings"
              )}
              </Box>
            </Box>
          )
        ) : (
          <>
            {renderTable("Stock de Oro", data.goldStock || [], color, "gold")}
            {renderTable(
              "Stock de Fornituras",
              data.findingsStock || [],
              color,
              "findings"
            )}
            {renderTable(
              "Trabajo en Proceso",
              data.workInProgress || [],
              color,
              "standard"
            )}
          </>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {/* Skeletons para la tabla de resumen */}
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: ESTADO_ORO_COLORS.SUMMARY_BG, borderRadius: 1, border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`, height: 220 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
            <Skeleton variant="text" width={150} height={24} />
          </Box>
          <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[...Array(7)].map((_, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY,
                        padding: "4px 8px",
                      }}>
                      <Skeleton variant="text" width="80%" height={20} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(SKELETON_CONFIG.summaryRows)].map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {[...Array(7)].map((_, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align={colIndex === 0 ? "center" : "center"}
                        sx={{
                          padding: "4px 8px",
                          backgroundColor: rowIndex === SKELETON_CONFIG.summaryRows - 1 ? ESTADO_ORO_COLORS.TOTAL_CELL_BG : "transparent",
                        }}>
                        <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Skeletons para las tablas de ubicación */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width={100} height={28} />
          <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1 }} />
        </Box>

        {/* Skeletons para las tablas de datos */}
        {[...Array(3)].map((_, tableIndex) => (
          <Card key={tableIndex} sx={{ mb: 0.5, boxShadow: 3, borderRadius: 2, minHeight: 400 }}>
            <CardContent sx={{ p: 1.5 }}>
              {/* Header del card */}
              <Box sx={{ minHeight: 40, mb: 0.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <Skeleton variant="circular" width={14} height={14} sx={{ mr: 0.5 }} />
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton variant="circular" width={24} height={24} sx={{ ml: 1 }} />
                  <Skeleton variant="text" width={150} height={16} sx={{ ml: 1 }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, flexWrap: "wrap" }}>
                  {[...Array(4)].map((_, chipIndex) => (
                    <Skeleton key={chipIndex} variant="rectangular" width={80} height={16} sx={{ borderRadius: 1 }} />
                  ))}
                </Box>
              </Box>

              {/* Tabla */}
              <TableContainer component={Paper} sx={{ borderRadius: 1, border: `1px solid ${ESTADO_ORO_COLORS.TABLE_BORDER}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[...Array(5)].map((_, i) => (
                        <TableCell
                          key={i}
                          sx={{
                            backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY,
                            padding: "4px 8px",
                          }}>
                          <Skeleton variant="text" width="70%" height={20} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(SKELETON_CONFIG.tableRows)].map((_, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {[...Array(5)].map((_, colIndex) => (
                          <TableCell
                            key={colIndex}
                            sx={{
                              padding: "4px 8px",
                              backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5",
                            }}>
                            <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {/* Fila de totales */}
                    <TableRow>
                      <TableCell sx={{ padding: "4px 8px", backgroundColor: "grey.100" }}>
                        <Skeleton variant="text" width="60%" height={SKELETON_CONFIG.height} />
                      </TableCell>
                      {[...Array(4)].map((_, i) => (
                        <TableCell
                          key={i}
                          sx={{ padding: "4px 8px", backgroundColor: "grey.100" }}>
                          <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Última actualización: {lastUpdate}
        </Typography>
      </Box>
    );
  }

  // Función para renderizar la tabla de resumen
  const renderSummaryTable = () => {
    const cordobaGoldStock = cordobaData.stock?.find(item => item.metal === "ORO")?.stock || 0;
    const cordobaPlatinumStock = cordobaData.stock?.find(item => item.metal === "PLATINO")?.stock || 0;
    
    const indiaGoldTotal = calculateTotals(indiaData.goldStock || [], "gold");
    const indiaPlatinumTotal = calculateTotals(indiaData.platinumStock || [], "platinum");
    const cordobaGoldRequired = dynamicRequiredMetals.cordoba.gold;
    const cordobaPlatinumRequired = dynamicRequiredMetals.cordoba.platinum;
    const euroshineGoldRequired = dynamicRequiredMetals.euroshine.gold;
    const euroshinePlatinumRequired = dynamicRequiredMetals.euroshine.platinum;
    
    // Calcular diferencias (Stock - Necesidad)
    const cordobaGoldDifference = parseFloat(cordobaGoldStock) - cordobaGoldRequired;
    const cordobaPlatinumDifference = parseFloat(cordobaPlatinumStock) - cordobaPlatinumRequired;
    const euroshineGoldDifference = indiaGoldTotal.pureWt - euroshineGoldRequired;
    const euroshinePlatinumDifference = indiaPlatinumTotal.pureWt - euroshinePlatinumRequired;
    

  return (
      <Box sx={{ mb: 2, p: 1.5, backgroundColor: ESTADO_ORO_COLORS.SUMMARY_BG, borderRadius: 1, border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`, height: 220 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <MaterialCommunityIcons 
            name="view-grid" 
            size={20} 
            color={ESTADO_ORO_COLORS.SUMMARY_ICON} 
            style={{ marginRight: 8 }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.8rem", color: ESTADO_ORO_COLORS.SUMMARY_TEXT }}>
            Resumen de Metales
          </Typography>
        </Box>
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.8rem", backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY, color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT }}>
                  Ubicación
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.8rem", backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY, color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT }}>
                  Oro 999 Stock (g)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.8rem", backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY, color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT }}>
                  Platino 999 Stock (g)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.8rem", backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY, color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT }}>
                  Oro 999 Requerido (g)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.8rem", backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY, color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT }}>
                  Platino 999 Requerido (g)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.8rem", backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY, color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT }}>
                  Oro 999 Disponible (g)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.8rem", backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY, color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT }}>
                  Platino 999 Disponible (g)
                </TableCell>
              </TableRow>
            </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: "bold", 
                    fontSize: "0.75rem", 
                    textAlign: "center"
                  }}>Córdoba</TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "success.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {parseFloat(cordobaGoldStock).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "success.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {parseFloat(cordobaPlatinumStock).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "error.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {cordobaGoldRequired.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "error.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {cordobaPlatinumRequired.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: cordobaGoldDifference >= 0 ? "success.main" : "error.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {cordobaGoldDifference.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: cordobaPlatinumDifference >= 0 ? "success.main" : "error.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {cordobaPlatinumDifference.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: "bold", 
                    fontSize: "0.75rem", 
                    textAlign: "center"
                  }}>Euroshine</TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "success.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {indiaGoldTotal.pureWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "success.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {indiaPlatinumTotal.pureWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "error.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {euroshineGoldRequired.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "error.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {euroshinePlatinumRequired.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: euroshineGoldDifference >= 0 ? "success.main" : "error.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {euroshineGoldDifference.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: euroshinePlatinumDifference >= 0 ? "success.main" : "error.main",
                      fontSize: "0.8rem"
                    }}
                  >
                    {euroshinePlatinumDifference.toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                </TableRow>
                
                {/* Fila de totales */}
                <TableRow sx={{ backgroundColor: ESTADO_ORO_COLORS.TOTAL_ROW_BG }}>
                  <TableCell sx={{ 
                    fontWeight: "bold", 
                    fontSize: "0.75rem", 
                    textAlign: "center",
                    backgroundColor: ESTADO_ORO_COLORS.TOTAL_CELL_BG
                  }}>
                    TOTAL
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "success.main",
                      fontSize: "0.8rem",
                      backgroundColor: ESTADO_ORO_COLORS.TOTAL_CELL_BG
                    }}
                  >
                    {(parseFloat(cordobaGoldStock) + indiaGoldTotal.pureWt).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "success.main",
                      fontSize: "0.8rem",
                      backgroundColor: ESTADO_ORO_COLORS.TOTAL_CELL_BG
                    }}
                  >
                    {(parseFloat(cordobaPlatinumStock) + indiaPlatinumTotal.pureWt).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "error.main",
                      fontSize: "0.8rem",
                      backgroundColor: ESTADO_ORO_COLORS.TOTAL_CELL_BG
                    }}
                  >
                    {(cordobaGoldRequired + euroshineGoldRequired).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "error.main",
                      fontSize: "0.8rem",
                      backgroundColor: ESTADO_ORO_COLORS.TOTAL_CELL_BG
                    }}
                  >
                    {(cordobaPlatinumRequired + euroshinePlatinumRequired).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: (cordobaGoldDifference + euroshineGoldDifference) >= 0 ? "success.main" : "error.main",
                      fontSize: "0.8rem",
                      backgroundColor: ESTADO_ORO_COLORS.TOTAL_CELL_BG
                    }}
                  >
                    {(cordobaGoldDifference + euroshineGoldDifference).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: (cordobaPlatinumDifference + euroshinePlatinumDifference) >= 0 ? "success.main" : "error.main",
                      fontSize: "0.8rem",
                      backgroundColor: ESTADO_ORO_COLORS.TOTAL_CELL_BG
                    }}
                  >
                    {(cordobaPlatinumDifference + euroshinePlatinumDifference).toLocaleString("es-ES", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}g
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
    );
  };

  // Mapeo seguro de detalles (excelDetailsData) a estructura para el calendario
  const mapExcelToCalendarDetails = (detailsArray) => {
    if (!Array.isArray(detailsArray)) return [];
    return detailsArray.map((item) => {
      const fechaRaw = item.fecha || item.date || item.Fecha || item.FECHA || item.orderDate || (item.fechaentrega && (item.fechaentrega.date || item.fechaentrega));
      const fecha = fechaRaw instanceof Date ? fechaRaw : new Date(fechaRaw);
      const goldReq =
        parseFloat(
          item.goldRequired ?? item.oroRequired ?? item.oro999Required ?? item.oro ?? item.Oro ?? 0
        ) || 0;
      const platReq =
        parseFloat(
          item.platinumRequired ?? item.platinoRequired ?? item.platino999Required ?? item.platino ?? item.Platino ?? 0
        ) || 0;
      const locRaw = (item.location || item.Loc_Desc || item.ubicacion || item.Ubicacion || "Cordoba");
      return {
        fecha,
        goldRequired: goldReq,
        platinumRequired: platReq,
        location: String(locRaw).toLowerCase().includes("euro") ? "Euroshine" : "Cordoba",
      };
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Encabezado principal: resumen + calendario compacto en dos columnas */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 220px" }, gap: "16px", alignItems: "start", width: "99%" }}>
        <Box>
          {renderSummaryTable()}
        </Box>
        {excelDetailsData.length > 0 && (
          <ComponentMetalsCalendar
            title=""
            compact
            size="xs"
            fixedHeight={230}            
            cellWidth={32}
            showTitle={false}
            baseStock={{
              cordobaGold: parseFloat(cordobaData.stock?.find(i => i.metal === "ORO")?.stock || 0),
              cordobaPlatinum: parseFloat(cordobaData.stock?.find(i => i.metal === "PLATINO")?.stock || 0),
              euroshineGold: calculateTotals(indiaData.goldStock || [], "gold").pureWt,
              euroshinePlatinum: calculateTotals(indiaData.platinumStock || [], "platinum").pureWt,
            }}
            details={mapExcelToCalendarDetails(excelDetailsData)}
            excelData={excelDetailsData}
            StockTotalOro={parseFloat(cordobaData.stock?.find(i => i.metal === "ORO")?.stock || 0) + calculateTotals(indiaData.goldStock || [], "gold").pureWt}
            StockTotalPlatino={parseFloat(cordobaData.stock?.find(i => i.metal === "PLATINO")?.stock || 0) + calculateTotals(indiaData.platinumStock || [], "platinum").pureWt}
          />
        )}
      </Box>

      {/* Tabla de filtros Excel */}
      {excelDetailsData.length > 0 && (
        <ComponentExcelFilters 
          data={excelDetailsData} 
          title="Detalles de Órdenes y Artículos"
          onRequiredMetalsChange={setDynamicRequiredMetals}
        />
      )}

      {/* Solo mostrar India - Córdoba está comentada */}
      <Box sx={{ flex: 1 }}>
        {renderLocationSection("India", indiaData, "secondary")}
      </Box>
    </Box>
  );
}
